// The only module that talks to the server. Every request carries the tab's id, so the server keeps one running
// query per tab and purpose (typing replaces the previous query instead of piling them up).

export interface WireLine {
    id: string;
    ts: string;
    env: string;
    service: string;
    instance: string;
    commit: string;
    level: string;
    module: string;
    logger: string;
    message: string;
    kind: string;
    request_id: string;
    user_id: string;
    error_uuid: string;
    exception: string;
    fingerprint: string;
    http_method: string;
    http_path: string;
    http_status: number;
    duration_ms: number;
    attributes?: Record<string, unknown>;
}

export interface User {
    id: string;
    email: string;
    name: string;
    role: 'admin' | 'member';
    // A password someone else set, replaced before anything else opens (D-29).
    mustChangePassword: boolean;
}

export interface Scope {
    env: string;
    q: string;
    from: number;
    to: number;
}

export interface SearchResponse {
    lines: WireLine[];
    cursor: string | null;
    errors: Array<{ message: string; start: number; end: number }>;
}

export interface HistogramResponse {
    stepSeconds: number;
    points: Array<{ t: number; counts: Record<string, number> }>;
}

export type FacetsResponse = Record<string, Array<{ value: string; count: number }>>;

export interface SourceStatus {
    id: string;
    name: string;
    env: string;
    lastAt: string | null;
    linesPerMinute: number;
    bytesPerMinute: number;
    rejected: Record<string, number>;
    services: Array<{ service: string; lastAt: string; linesPerMinute: number }>;
}

export interface SavedView {
    id: string;
    name: string;
    env: string | null;
    q: string;
    range: string;
    columns: string[];
    pinned: boolean;
    updated_at: string;
    author: string | null;
}

export type NewView = Omit<SavedView, 'id' | 'updated_at' | 'author'>;

export interface SourceRow {
    id: string;
    name: string;
    env: string;
    profile: 'bustard' | 'generic';
    default_service: string | null;
    retention_days: number;
    token_hint: string;
    created_at: string;
    revoked_at: string | null;
    last_seen_at: string | null;
}

// A machine's token to read (/api/v1 and the stream), for some environments or every one.
export interface ReadTokenRow {
    id: string;
    name: string;
    envs: '*' | string[];
    token_hint: string;
    created_at: string;
    revoked_at: string | null;
    last_used_at: string | null;
    author: string | null;
}

export interface Person {
    id: string;
    email: string;
    name: string;
    role: 'admin' | 'member';
    must_change_password: boolean;
    created_at: string;
    last_login_at: string | null;
}

export interface Pattern {
    template: string;
    count: number;
    errors: number;
    warnings: number;
    level: string;
    services: string[];
    firstSeen: number;
    lastSeen: number;
    sampleId: string;
}

export interface Overview {
    stepMs: number;
    totals: { lines: number; errors: number; warnings: number; requests: number; users: number; services: number };
    volume: Array<{ t: number; service: string; lines: number; requests: number; errors: number; warnings: number }>;
    deploys: Array<{ service: string; commit: string; at: number }>;
    topErrors: Array<{ fingerprint: string; title: string; message: string; count: number; users: number; lastSeen: number }>;
    routes: Array<{ method: string; path: string; count: number }>;
    statuses: Array<{ status: number; count: number }>;
    cron: Array<{ job: string; runs: number; errors: number; lastRun: number; avgMs: number; maxMs: number }>;
    modules: Array<{ module: string; lines: number; errors: number; warnings: number }>;
}

export interface AlertRule {
    id: string;
    name: string;
    env: string;
    kind: 'threshold' | 'absence' | 'new_issue' | 'relative';
    q: string;
    // The most lines for a threshold; for a relative rule, the fewest lines worth an alert.
    threshold: number;
    direction: 'up' | 'down';
    percent: number;
    compareDays: number;
    windowMinutes: number;
    confirmChecks: number;
    recoverChecks: number;
    channelIds: string[];
    enabled: boolean;
    mutedUntil: string | null;
    state: 'ok' | 'pending' | 'firing';
    lastValue: number | null;
    // A relative rule's count in the window it compared with (null: nothing that far back yet).
    lastBaseline: number | null;
    lastEvaluatedAt: string | null;
    stateSince: string | null;
}

export type NewAlertRule = Pick<
    AlertRule,
    | 'name'
    | 'env'
    | 'kind'
    | 'q'
    | 'threshold'
    | 'direction'
    | 'percent'
    | 'compareDays'
    | 'windowMinutes'
    | 'confirmChecks'
    | 'recoverChecks'
    | 'channelIds'
>;

export interface AlertChannel {
    id: string;
    name: string;
    kind: 'slack' | 'webhook';
    address: string;
}

export interface AlertEvent {
    id: string;
    kind: 'fired' | 'resolved' | 'new_issue' | 'test';
    at: string;
    value: number | null;
    fingerprint: string | null;
    title: string | null;
    delivered: boolean;
    error: string | null;
    rule_id: string;
    rule_name: string;
    env: string;
}

export interface Storage {
    envs: Array<{ env: string; rows: number; diskBytes: number; rawBytes: number; parts: number; firstAt: number; lastAt: number }>;
    disk: { freeBytes: number; totalBytes: number };
    memory: { usedBytes: number; capBytes: number };
    version: string;
    uptimeSeconds: number;
}

export type IssueStatus = 'open' | 'regressed' | 'acknowledged' | 'resolved' | 'muted';

export interface Issue {
    fingerprint: string;
    title: string;
    exception: string;
    module: string;
    logger: string;
    message: string;
    template: string;
    services: string[];
    level: 'error' | 'fatal';
    count: number;
    users: number;
    firstSeen: number;
    lastSeen: number;
    lastId: string;
    firstCommit: string;
    lastCommit: string;
    sparkline: number[];
    status: IssueStatus;
    note: string | null;
    mutedUntil: string | null;
    firstEver: number;
    firstEverCommit: string;
    isNew: boolean;
}

export interface IssueDetail {
    issue: Issue | null;
    routes: Array<{ method: string; path: string; count: number }>;
    users: Array<{ user: string; count: number }>;
    commits: Array<{ commit: string; count: number; firstMs: number; lastMs: number }>;
    instances: number;
}

export class ApiError extends Error {
    constructor(
        readonly status: number,
        message: string,
    ) {
        super(message);
    }
}

const TAB_ID = crypto.randomUUID().slice(0, 8);

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(path, {
        ...init,
        credentials: 'same-origin',
        headers: { 'x-tab-id': TAB_ID, ...(init.body ? { 'content-type': 'application/json' } : {}), ...init.headers },
    });
    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string };
        throw new ApiError(response.status, body.error ?? response.statusText);
    }

    return (await response.json()) as T;
}

const params = (values: object): string =>
    new URLSearchParams(
        Object.entries(values as Record<string, unknown>).flatMap(([key, value]) =>
            value === null || value === undefined ? [] : [[key, String(value)]],
        ),
    ).toString();

export const api = {
    me: (): Promise<{ user: User }> => request('/api/auth/me'),
    login: (email: string, password: string): Promise<{ user: User }> =>
        request('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    logout: (): Promise<unknown> => request('/api/auth/logout', { method: 'POST' }),
    envs: (): Promise<{ envs: string[] }> => request('/api/envs'),
    status: (): Promise<{ sources: SourceStatus[] }> => request('/api/status'),
    search: (scope: Scope, cursor: string | null, signal?: AbortSignal): Promise<SearchResponse> =>
        request(`/api/search?${params({ ...scope, limit: 200, cursor })}`, { signal }),
    histogram: (scope: Scope, signal?: AbortSignal): Promise<HistogramResponse> =>
        request(`/api/histogram?${params({ ...scope, buckets: 60 })}`, { signal }),
    facets: (scope: Scope, signal?: AbortSignal): Promise<FacetsResponse> => request(`/api/facets?${params(scope)}`, { signal }),
    overview: (scope: Scope, signal?: AbortSignal): Promise<Overview> => request(`/api/overview?${params(scope)}`, { signal }),
    storage: (): Promise<Storage> => request('/api/storage'),
    patterns: (scope: Scope, requests: boolean, signal?: AbortSignal): Promise<{ patterns: Pattern[]; total: number }> =>
        request(`/api/patterns?${params({ ...scope, requests: requests ? 1 : 0, limit: 200 })}`, { signal }),
    line: (env: string, id: string): Promise<{ line: WireLine }> => request(`/api/lines/${id}?${params({ env })}`),
    context: (env: string, id: string, n: number, signal?: AbortSignal): Promise<{ before: WireLine[]; after: WireLine[] }> =>
        request(`/api/lines/${id}/context?${params({ env, n })}`, { signal }),
    find: (value: string, signal?: AbortSignal): Promise<{ results: Array<WireLine & { matched: string }> }> =>
        request(`/api/find?${params({ value })}`, { signal }),

    views: (): Promise<{ views: SavedView[] }> => request('/api/views'),
    saveView: (view: NewView): Promise<{ id: string }> => request('/api/views', { method: 'POST', body: JSON.stringify(view) }),
    updateView: (id: string, patch: Partial<NewView>): Promise<unknown> =>
        request(`/api/views/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deleteView: (id: string): Promise<unknown> => request(`/api/views/${id}`, { method: 'DELETE' }),

    sources: (): Promise<{ sources: SourceRow[] }> => request('/api/sources'),
    createSource: (source: {
        name: string;
        env: string;
        profile: 'bustard' | 'generic';
        retentionDays: number;
    }): Promise<{ source: { id: string }; token: string }> => request('/api/sources', { method: 'POST', body: JSON.stringify(source) }),
    rotateSource: (id: string): Promise<{ token: string }> => request(`/api/sources/${id}/rotate`, { method: 'POST' }),
    updateSource: (id: string, patch: { name?: string; retentionDays?: number }): Promise<unknown> =>
        request(`/api/sources/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    revokeSource: (id: string): Promise<unknown> => request(`/api/sources/${id}`, { method: 'DELETE' }),
    tokens: (): Promise<{ tokens: ReadTokenRow[] }> => request('/api/tokens'),
    createToken: (token: { name: string; envs: '*' | string[] }): Promise<{ id: string; token: string }> =>
        request('/api/tokens', { method: 'POST', body: JSON.stringify(token) }),
    revokeToken: (id: string): Promise<unknown> => request(`/api/tokens/${id}`, { method: 'DELETE' }),

    people: (): Promise<{ users: Person[] }> => request('/api/users'),
    addPerson: (person: { email: string; name: string; role: 'admin' | 'member' }): Promise<{ id: string; password: string }> =>
        request('/api/users', { method: 'POST', body: JSON.stringify(person) }),
    updatePerson: (id: string, patch: { name?: string; role?: 'admin' | 'member' }): Promise<unknown> =>
        request(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    resetPassword: (id: string): Promise<{ password: string }> => request(`/api/users/${id}/password`, { method: 'POST' }),
    removePerson: (id: string): Promise<unknown> => request(`/api/users/${id}`, { method: 'DELETE' }),
    issues: (scope: Scope, signal?: AbortSignal): Promise<{ issues: Issue[]; stepMs: number }> => request(`/api/issues?${params(scope)}`, { signal }),
    issue: (fingerprint: string, scope: Scope, signal?: AbortSignal): Promise<IssueDetail> =>
        request(`/api/issues/${fingerprint}?${params(scope)}`, { signal }),
    setIssueState: (
        fingerprint: string,
        state: { env: string; status: 'open' | 'acknowledged' | 'resolved' | 'muted'; mutedHours?: number | null; note?: string | null },
    ): Promise<unknown> => request(`/api/issues/${fingerprint}/state`, { method: 'POST', body: JSON.stringify(state) }),
    alerts: (): Promise<{ rules: AlertRule[]; channels: AlertChannel[]; events: AlertEvent[] }> => request('/api/alerts'),
    firing: (): Promise<{ rules: Array<{ id: string; name: string; env: string; state_since: string | null }> }> => request('/api/alerts/firing'),
    createRule: (rule: NewAlertRule): Promise<{ id: string }> => request('/api/alerts/rules', { method: 'POST', body: JSON.stringify(rule) }),
    updateRule: (id: string, patch: Partial<NewAlertRule> & { enabled?: boolean; mutedHours?: number | null }): Promise<unknown> =>
        request(`/api/alerts/rules/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deleteRule: (id: string): Promise<unknown> => request(`/api/alerts/rules/${id}`, { method: 'DELETE' }),
    createChannel: (channel: { name: string; kind: 'slack' | 'webhook'; url: string }): Promise<{ id: string }> =>
        request('/api/alerts/channels', { method: 'POST', body: JSON.stringify(channel) }),
    deleteChannel: (id: string): Promise<unknown> => request(`/api/alerts/channels/${id}`, { method: 'DELETE' }),
    testChannel: (id: string): Promise<unknown> => request(`/api/alerts/channels/${id}/test`, { method: 'POST' }),
    // With `compare` (days), each point's `base` is the same window that many days earlier, and `firstAt` the environment's
    // first line.
    alertPreview: (
        query: { env: string; q: string; window: number; hours: number; compare?: number },
        signal?: AbortSignal,
    ): Promise<{ stepMs: number; points: Array<{ t: number; count: number; base?: number }>; firstAt?: number | null }> =>
        request(`/api/alerts/preview?${params(query)}`, { signal }),
    changePassword: (current: string, next: string): Promise<{ user: User }> =>
        request('/api/auth/password', { method: 'POST', body: JSON.stringify({ current, next }) }),
};

export function liveSocket(env: string, q: string): WebSocket {
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';

    return new WebSocket(`${protocol}://${location.host}/api/live?${params({ env, q })}`);
}
